// Auto winner-screen director — the console-side half of the match-end
// pipeline. The server half is POST /api/packages/[slug]/winner (stamps the
// finished set's winner into package_state.winner); this module decides WHEN.
//
// While armed and OBS is connected, it polls the user's own in-match token
// stream endpoint — the exact endpoint the OBS in-match source polls, so the
// director always watches the same stream the overlays render (slug/stream
// resolve server-side from overlay_configs, immune to unsaved console form
// state). Two edges drive it:
//
//   banner-hide (found true → false): the set left the stream queue — the
//   only reliable match-end signal. start.gg usually advances the queue the
//   instant the final game is reported, so a set rarely ever polls as
//   completed (the reason the server's #139 edge works the same way).
//   Stamp the winner, then cut to the Winner Screen — but only when the
//   program scene is still the In-Match scene: never yank a manual choice.
//
//   next set live (found false → true): cut back to In-Match — only when
//   the program is currently the Winner Screen, same rule.
//
// A setId swap with found staying true means the next set went live within
// one poll gap: the banner never hid, so no scene changes — the departed
// set's winner is stamped quietly and the watch moves on. A completed set
// still sitting in the queue gets an early stamp (data ready before the
// eventual cut); a stamped set re-entering the queue (TO reopening it to fix
// a score) re-arms so its next departure re-stamps corrected data.
//
// Arm state is per (machine, package) in localStorage next to the OBS
// connection config: arming is a rig decision, not show data — storing it in
// package_state would arm every browser that opens the console.
//
// Scene changes are direct program cuts even in Studio Mode — preview
// staging is for humans; automation taking over the preview bus mid-show
// would be worse than a clean cut.

import { api } from '../api/client.js';
import { obsControl } from './obsControl.js';

const armStorageKey = (packageSlug) => `streamgg.obs.director.${packageSlug}`;

const IN_MATCH_KEY = 'in-match';
const WINNER_KEY = 'winner-screen';

// Same cadence as the overlays (PackageProduction's 5s contract). Errors and
// rate-limit responses back off instead of retry-storming the shared token.
const POLL_MS = 5000;
const ERROR_BACKOFF_MS = 15_000;
const AUTH_BACKOFF_MS = 60_000;
const RATE_LIMIT_FALLBACK_S = 30;
// The cut waits for the winner stamp so the screen airs with fresh data —
// but a slow start.gg lookup must not hold the cut hostage. Past the cap we
// cut anyway; the stamp lands via the overlay's own 5s poll.
const STAMP_WAIT_CAP_MS = 8_000;

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled — arming just won't persist */
  }
};

class ObsDirector {
  constructor() {
    this.packageSlug = null;
    this.streamPath = null;
    this.available = false;
    this.reason = 'not configured';
    this.armed = false;
    this.watching = false;
    this.liveSetId = null;
    this.lastAction = '';

    this._lastFound = false;
    this._stampedSetId = null;
    this._timer = null;
    this._pollSeq = 0;
    this._listeners = new Set();
    this._snap = null;
    this._rebuildSnapshot();

    // Start/stop tracks the OBS connection: a disconnected rig can't switch
    // scenes, and pausing the watch also stops spending stream-poll quota.
    obsControl.subscribe(() => this._sync());

    this.subscribe = this.subscribe.bind(this);
    this.getSnapshot = this.getSnapshot.bind(this);
  }

  // ── subscription (useSyncExternalStore contract) ─────────────────────────

  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  getSnapshot() {
    return this._snap;
  }

  _rebuildSnapshot() {
    this._snap = {
      armed: this.armed,
      available: this.available,
      reason: this.reason,
      watching: this.watching,
      liveSetId: this.liveSetId,
      lastAction: this.lastAction,
    };
  }

  _emit() {
    this._rebuildSnapshot();
    for (const listener of this._listeners) listener();
  }

  // ── config ───────────────────────────────────────────────────────────────

  // Called from PackageProduction whenever the granted overlays resolve.
  // The director needs the in-match grant's token (its stream endpoint is
  // the watch source) and a winner-screen grant (something to cut to).
  configure({ packageSlug, streamPath, hasWinnerOverlay, onStamp }) {
    const changed = packageSlug !== this.packageSlug;
    this.packageSlug = packageSlug;
    this.streamPath = streamPath ?? null;
    // Lets the console fold a successful stamp into its local form state —
    // without it, the next autosave's full-document PUT would write the
    // pre-stamp winner back over what the director just stored.
    this._onStamp = onStamp ?? null;
    if (!streamPath) {
      this.available = false;
      this.reason = 'needs the In-Match overlay grant';
    } else if (!hasWinnerOverlay) {
      this.available = false;
      this.reason = 'needs the Winner Screen overlay grant';
    } else {
      this.available = true;
      this.reason = '';
    }
    this.armed =
      this.available && readJson(armStorageKey(packageSlug))?.armed === true;
    if (changed) this._resetEdges();
    this._sync();
  }

  setArmed(on) {
    if (!this.packageSlug) return;
    this.armed = on === true && this.available;
    writeJson(armStorageKey(this.packageSlug), { armed: this.armed });
    obsControl.note(true, `auto winner screen ${this.armed ? 'ARMED' : 'disarmed'}`);
    this._sync();
  }

  // ── watch lifecycle ──────────────────────────────────────────────────────

  _sync() {
    const shouldWatch = this.armed && this.available && obsControl.isConnected;
    if (shouldWatch && !this.watching) {
      this.watching = true;
      // Fresh watch = fresh memory: a set that departed while we weren't
      // looking (OBS down, disarmed) must not fire a late winner cut.
      this._resetEdges();
      this.lastAction = 'watching the stream';
      this._schedule(0);
    } else if (!shouldWatch && this.watching) {
      this.watching = false;
      this._pollSeq += 1;
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
      this.lastAction = '';
    }
    this._emit();
  }

  _resetEdges() {
    this.liveSetId = null;
    this._lastFound = false;
    this._stampedSetId = null;
  }

  _schedule(delayMs) {
    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this._timer = null;
      this._poll();
    }, delayMs);
  }

  async _poll() {
    const seq = ++this._pollSeq;
    let next = POLL_MS;
    try {
      const res = await fetch(this.streamPath, { cache: 'no-store' });
      if (seq !== this._pollSeq || !this.watching) return;
      if (res.status === 401) {
        // Key rotated / grant revoked — long backoff instead of log spam.
        obsControl.note(false, 'director: stream poll rejected (401) — is the overlay key current?');
        next = AUTH_BACKOFF_MS;
      } else if (!res.ok) {
        next = ERROR_BACKOFF_MS;
      } else {
        const data = await res.json();
        if (data?.rateLimited) {
          next = Math.max(5, Number(data.retryAfter) || RATE_LIMIT_FALLBACK_S) * 1000;
        } else {
          await this._onSample(data);
        }
      }
    } catch {
      next = ERROR_BACKOFF_MS;
    }
    if (this.watching && seq === this._pollSeq) this._schedule(next);
  }

  // ── the edge machine ─────────────────────────────────────────────────────

  async _onSample(data) {
    if (data?.tester) {
      // Tester mode (licensing): the stream is the animated sample card —
      // its sets aren't stampable, and scene automation against demo data
      // would fight the operator mid-rehearsal. Stand down quietly; the
      // next licensed poll re-engages from a clean slate.
      this._resetEdges();
      this.lastAction = 'tester mode — sample data on stream';
      this._emit();
      return;
    }
    if (data?.found) {
      const id = data.setId != null ? String(data.setId) : null;
      const wasFound = this._lastFound;
      this._lastFound = true;
      if (id == null) return; // pre-deploy server without setId — inert

      if (!wasFound) this._returnToMatch();

      if (this.liveSetId != null && id !== this.liveSetId) {
        // Departed + replaced inside one poll gap: banner never hid.
        if (this._stampedSetId !== this.liveSetId) {
          await this._stamp(this.liveSetId, 'replaced on stream');
        }
      }
      // A stamped set back on stream = the TO reopened it; re-arm so its
      // next departure re-stamps the corrected result.
      if (id === this._stampedSetId && !data.completed) {
        this._stampedSetId = null;
      }
      if (data.completed && this._stampedSetId !== id) {
        // Rare lingering-completed sighting — stamp early, cut later at the
        // banner-hide edge as usual.
        await this._stamp(id, 'reported complete');
      }
      this.liveSetId = id;
      this.lastAction = `watching set ${id}`;
      this._emit();
      return;
    }

    // found:false — the banner-hide edge if we were watching a set.
    const departed = this._lastFound ? this.liveSetId : null;
    this._lastFound = false;
    this.liveSetId = null;
    if (departed != null) {
      const stampP =
        this._stampedSetId !== departed
          ? this._stamp(departed, 'left the stream queue')
          : Promise.resolve();
      await Promise.race([
        stampP,
        new Promise((r) => setTimeout(r, STAMP_WAIT_CAP_MS)),
      ]);
      // Disarmed / disconnected while the stamp was in flight → no cut.
      if (this.watching) this._cutToWinner();
    }
    this._emit();
  }

  async _stamp(setId, why) {
    // Optimistic mark: a departure sample racing a replaced-stamp must not
    // double-post the same set.
    this._stampedSetId = setId;
    this.lastAction = `stamping winner of set ${setId}`;
    this._emit();
    try {
      const res = await api.packageStampWinner(this.packageSlug, setId);
      if (res?.ok) {
        const w = res.winner ?? {};
        const who = `${w.prefix ? `${w.prefix} ` : ''}${w.name || 'unknown'}`;
        obsControl.note(true, `director: winner stamped — ${who}${w.character ? ` (${w.character})` : ''} · ${why}`);
        try { this._onStamp?.(w); } catch { /* console callback must never break the pipeline */ }
      } else {
        obsControl.note(false, `director: set ${setId} has no winner to stamp (${res?.reason ?? 'unknown'})`);
      }
    } catch (err) {
      obsControl.note(false, `director: winner stamp failed — ${err.message}`);
    }
  }

  _cutToWinner() {
    const inMatchScene = obsControl.sceneMap[IN_MATCH_KEY];
    const current = obsControl.currentProgramScene;
    if (!inMatchScene || current !== inMatchScene) {
      obsControl.note(true, `director: match ended, but program is ${current ? `"${current}"` : 'unknown'} — leaving scenes alone`);
      return;
    }
    obsControl.setScene(WINNER_KEY); // logs its own outcome
  }

  _returnToMatch() {
    const winnerScene = obsControl.sceneMap[WINNER_KEY];
    if (!winnerScene || obsControl.currentProgramScene !== winnerScene) return;
    obsControl.note(true, 'director: next set is live — returning to In-Match');
    obsControl.setScene(IN_MATCH_KEY);
  }
}

export const obsDirector = new ObsDirector();
